import React, { useRef, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { UploadCloud, X, FileText, Image as ImageIcon, AlertCircle, Loader2, Ellipsis, Download, RefreshCcw, Trash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { showErrorToast, showSuccessToast } from '../toast/toast';
import { backendApiUrl } from '@/lib/config';
import oktaService from '@/services/OktaService';
import FullscreenViewDialog from '../fullscreen-view-dialog';
import { processFileForUpload } from '@/utils/imageCompression';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Enhanced MediaItem interface to support PDF and metadata
interface MediaItem {
    id: number;
    url: string;
    contentType: 'image' | 'video' | 'pdf';
    type: string; // Should be 'unit-load-images'
    palletId: number;
    file?: File;
    localUrl?: string;
    isLoading?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    subCategory?: 'specNumber' | 'palletDrawing' | 'topView';
    originalFilename?: string;
    fileSize?: number;
    metadata?: Record<string, any>;
    processingCompress?: boolean; // Add flag for compression in progress
}

interface UnitLoadImageUploaderProps {
    palletId: number;
    category: string; // Will be 'unit-load-images'
    mediaItems: MediaItem[];
    onAddMedia: (newItem: MediaItem) => void;
    onDeleteMedia: (mediaId: number) => void;
}

// Define the structure for the three specific upload slots with accept types
const uploadSlots: {
    key: NonNullable<MediaItem['subCategory']>;
    title: string;
    description: string;
    acceptTypes: string;
    maxSize: number; // in bytes
    icon: React.ReactNode;
}[] = [
        {
            key: 'specNumber',
            title: 'Unit Load Specification Number',
            description: 'Upload PDF or image (max 4MB)',
            acceptTypes: 'application/pdf,image/*',
            maxSize: 4 * 1024 * 1024, // 4MB
            icon: <FileText size={48} className="mx-auto mb-2" />
        },
        {
            key: 'palletDrawing',
            title: 'Pallet Drawing',
            description: 'Upload PDF or image (max 4MB)',
            acceptTypes: 'application/pdf,image/*',
            maxSize: 4 * 1024 * 1024, // 4MB
            icon: <FileText size={48} className="mx-auto mb-2" />
        },
        {
            key: 'topView',
            title: 'Top View',
            description: 'Upload image only (max 4MB)',
            acceptTypes: 'image/*',
            maxSize: 4 * 1024 * 1024, // 4MB
            icon: <ImageIcon size={48} className="mx-auto mb-2" />
        },
    ];

const UnitLoadImageUploader: React.FC<UnitLoadImageUploaderProps> = ({
    palletId,
    category,
    mediaItems,
    refetch,
    onAddMedia,
    onDeleteMedia
}) => {
    // State to hold the media item for each slot (keyed by subCategory)
    const [slotMedia, setSlotMedia] = useState<Record<NonNullable<MediaItem['subCategory']>, MediaItem | null>>({
        specNumber: null,
        palletDrawing: null,
        topView: null,
    });

    // State to track uploads in progress for each slot
    const [uploading, setUploading] = useState<Record<NonNullable<MediaItem['subCategory']>, boolean>>({
        specNumber: false,
        palletDrawing: false,
        topView: false,
    });

    // Refs for the hidden file inputs
    const fileInputRefs = {
        specNumber: useRef<HTMLInputElement>(null),
        palletDrawing: useRef<HTMLInputElement>(null),
        topView: useRef<HTMLInputElement>(null),
    };

    // Add image blob cache for preloading - similar to upload-handler
    const imageBlobCache = useRef<Map<string, string>>(new Map());

    // Debug settings - same as in upload-handler
    const DEBUG_IMAGE_LOADING = false; // Set to true for detailed logs
    const FORCE_RELOAD_IMAGES = false; // Set to true to bypass cache and reload all images

    // Function to preload image and create a local blob URL
    const preloadImage = async (item: MediaItem, subCategory: NonNullable<MediaItem['subCategory']>) => {
        // Skip if item doesn't have a URL or is local file
        if (!item.url || item.file) return;

        // Max number of retries
        const MAX_RETRIES = 2;
        let retryCount = 0;
        let lastError: any = null;

        // Try loading the image with retries
        while (retryCount <= MAX_RETRIES) {
            try {
                // Check if we already have this cached (unless we're forcing a reload)
                if (!FORCE_RELOAD_IMAGES && imageBlobCache.current.has(item.url)) {
                    if (DEBUG_IMAGE_LOADING) {
                        console.log(`Using cached image for: ${item.url}`);
                    }
                    updateSlotMediaLocalUrl(subCategory, imageBlobCache.current.get(item.url) as string);
                    return;
                }

                // Get the URL that points to our backend
                const fullUrl = getFullMediaUrl(item.url);

                if (DEBUG_IMAGE_LOADING || retryCount > 0) {
                    console.log(`Preloading image from: ${fullUrl}${retryCount > 0 ? ` (retry ${retryCount}/${MAX_RETRIES})` : ''}`);
                }

                // Get Okta token for authorization
                let headers: Record<string, string> = {};
                try {
                    const oktaUser = await oktaService.getUser();
                    if (oktaUser.access_token) {
                        headers = {
                            'Authorization': `Bearer ${oktaUser.access_token}`
                        };
                        if (DEBUG_IMAGE_LOADING) {
                            console.log('Added authorization header for image fetch');
                        }
                    }
                } catch (tokenError) {
                    console.warn('Could not get Okta token for image fetch:', tokenError);
                    // Continue without auth header - might still work for public resources
                }

                const response = await fetch(fullUrl, {
                    headers,
                    credentials: 'include', // Include cookies if needed
                    cache: FORCE_RELOAD_IMAGES ? 'no-store' : 'default' // Don't use cached responses when forcing reload
                });

                if (!response.ok) {
                    // For 401/403 errors, we might need a new token, so retry
                    if (response.status === 401 || response.status === 403) {
                        throw new Error(`Authentication error: ${response.status} ${response.statusText}`);
                    }

                    throw new Error(`Failed to preload image: ${response.status} ${response.statusText}`);
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                // Cache this URL for future use
                imageBlobCache.current.set(item.url, blobUrl);

                // Update the slot media with the local URL
                updateSlotMediaLocalUrl(subCategory, blobUrl);

                if (DEBUG_IMAGE_LOADING) {
                    console.log(`Successfully loaded image: ${item.url}`);
                }

                // Success, exit the retry loop
                return;
            } catch (error) {
                lastError = error;
                console.error(`Error preloading image (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, error);

                if (retryCount < MAX_RETRIES) {
                    // Exponential backoff delay between retries (300ms, then 900ms)
                    const delay = 300 * Math.pow(3, retryCount);
                    console.log(`Retrying in ${delay}ms...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    retryCount++;
                } else {
                    // All retries failed
                    break;
                }
            }
        }

        // All attempts failed
        console.error(`All ${MAX_RETRIES + 1} attempts to load image failed`, lastError);
        updateSlotMediaLoadingState(subCategory, false, true, lastError?.message || 'Failed to load image');
    };

    // Helper to update a specific slot's local URL
    const updateSlotMediaLocalUrl = (subCategory: NonNullable<MediaItem['subCategory']>, localUrl: string) => {
        setSlotMedia(prev => {
            const item = prev[subCategory];
            if (!item) return prev;

            return {
                ...prev,
                [subCategory]: {
                    ...item,
                    localUrl,
                    isLoading: false,
                    hasError: false
                }
            };
        });
    };

    // Helper to update a specific slot's loading state
    const updateSlotMediaLoadingState = (
        subCategory: NonNullable<MediaItem['subCategory']>,
        isLoading: boolean,
        hasError: boolean = false,
        errorMessage?: string
    ) => {
        setSlotMedia(prev => {
            const item = prev[subCategory];
            if (!item) return prev;

            return {
                ...prev,
                [subCategory]: {
                    ...item,
                    isLoading,
                    hasError,
                    errorMessage: hasError ? (errorMessage || 'Error loading media') : undefined
                }
            };
        });
    };

    // Effect to populate the slots based on the incoming mediaItems prop
    useEffect(() => {
        const initialSlots: Record<NonNullable<MediaItem['subCategory']>, MediaItem | null> = {
            specNumber: null,
            palletDrawing: null,
            topView: null,
        };

        mediaItems.forEach(item => {
            if (item.subCategory && initialSlots[item.subCategory] === null) {
                // Only take the first one found for each slot
                initialSlots[item.subCategory] = {
                    ...item,
                    localUrl: item.file ? URL.createObjectURL(item.file) : undefined,
                    isLoading: !item.file && !item.url.startsWith('blob:'),
                    hasError: false
                };
            }
        });

        setSlotMedia(initialSlots);

        // Preload images for items that need it
        Object.entries(initialSlots).forEach(([subCategory, item]) => {
            if (item && (item.isLoading || (FORCE_RELOAD_IMAGES && !item.file))) {
                preloadImage(item, subCategory as NonNullable<MediaItem['subCategory']>);
            }
        });

        // Cleanup object URLs
        return () => {
            Object.values(initialSlots).forEach(item => {
                if (item?.localUrl && item.localUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(item.localUrl);
                }
            });
            // Also clear the cache map on unmount
            imageBlobCache.current.forEach(url => {
                if (url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
            imageBlobCache.current.clear();
        };
    }, [mediaItems]);

    const handleCardClick = (subCategory: NonNullable<MediaItem['subCategory']>) => {
        if (!uploading[subCategory]) {
            fileInputRefs[subCategory].current?.click();
        }
    };

    // Function to get file preview URL (blob URL or regular URL)
    const getPreviewUrl = (file: File) => {
        return URL.createObjectURL(file);
    };

    // Function to check if file is valid (type and size)
    const validateFile = (file: File, subCategory: NonNullable<MediaItem['subCategory']>) => {
        const slot = uploadSlots.find(s => s.key === subCategory);
        if (!slot) return { valid: false, error: 'Invalid slot type' };

        // For image files that can be compressed, we'll skip the size check
        // and handle it at upload time
        const isCompressibleImage = file.type.startsWith('image/') && file.type !== 'image/gif';

        // Check file size only for non-compressible files
        if (!isCompressibleImage && file.size > slot.maxSize) {
            return {
                valid: false,
                error: `File is too large. Maximum size is ${slot.maxSize / (1024 * 1024)}MB`
            };
        }

        // Still enforce file type checks for all files
        if (subCategory === 'topView' && !file.type.startsWith('image/')) {
            return { valid: false, error: 'Top View must be an image file' };
        }

        if ((subCategory === 'specNumber' || subCategory === 'palletDrawing')) {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                return {
                    valid: false,
                    error: `${subCategory === 'specNumber' ? 'Unit Load Specification' : 'Pallet Drawing'} must be a PDF or image file`
                };
            }
        }

        return { valid: true, error: null };
    };

    // Handle upload to server with progress tracking
    const uploadFile = async (file: File, subCategory: NonNullable<MediaItem['subCategory']>) => {
        try {
            // Set uploading state
            setUploading(prev => ({ ...prev, [subCategory]: true }));

            // Process the file based on its type
            const isImage = file.type.startsWith('image/') && file.type !== 'image/gif';
            let processedFile = file;

            // Process the file (compress if needed)
            if (isImage) {
                // For unit-load-images slots, we have a 4MB limit
                const slotInfo = uploadSlots.find(s => s.key === subCategory);
                const slotLimit = slotInfo?.maxSize || 4 * 1024 * 1024;

                // Update UI to show compression state if needed
                if (file.size > slotLimit) {
                    setSlotMedia(prev => {
                        const item = prev[subCategory];
                        if (!item) return prev;

                        return {
                            ...prev,
                            [subCategory]: {
                                ...item,
                                processingCompress: true
                            }
                        };
                    });
                }

                processedFile = await processFileForUpload(file, slotLimit);
            }

            // Check if file is still too large for absolute API limit
            const API_MAX_SIZE = 5 * 1024 * 1024; // 5MB absolute limit
            if (processedFile.size > API_MAX_SIZE) {
                throw new Error(`File is too large even after compression (${(processedFile.size / (1024 * 1024)).toFixed(2)}MB). Maximum allowed size is 5MB.`);
            }

            // Get Okta token for authorization
            const oktaUser = await oktaService.getUser();
            if (!oktaUser || !oktaUser.access_token) {
                throw new Error('Not authenticated. Please log in again.');
            }

            // Create form data
            const formData = new FormData();
            formData.append('media', processedFile);
            formData.append('subCategory', subCategory);
            formData.append('palletId', palletId.toString());
            formData.append('imageType', 'unit-load-images');

            // Send the file to the server
            const response = await fetch(`${backendApiUrl}/media/upload-file`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `Bearer ${oktaUser.access_token}`,
                },
                credentials: 'include',
            });

            if (!response.ok) {
                let errorMessage = 'Failed to upload file';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (e) {
                    // If we can't parse JSON, use the status text
                    errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Create a MediaItem from the response
            const mediaItem: MediaItem = {
                id: data.mediaId, // Server-generated ID
                url: data.url,
                contentType: data.contentType || getContentTypeFromFile(processedFile),
                type: 'unit-load-images',
                palletId: palletId,
                localUrl: getPreviewUrl(processedFile), // Keep local preview
                subCategory: subCategory,
                originalFilename: processedFile.name,
                fileSize: processedFile.size,
                metadata: {
                    blobName: data.blobName,
                    uploadedAt: new Date().toISOString()
                },
                isLoading: false,
                hasError: false,
                processingCompress: false
            };

            // Update local state
            setSlotMedia(prev => ({ ...prev, [subCategory]: mediaItem }));

            // Notify parent component (important for state management)
            onAddMedia(mediaItem);
            refetch()
            // Show success message
            showSuccessToast(`${getSlotTitle(subCategory)} uploaded successfully`);

            return mediaItem;
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown upload error';

            // Create error state in the slot
            const errorItem: MediaItem = {
                id: Date.now(), // Temporary ID
                url: '',
                contentType: getContentTypeFromFile(file),
                type: 'unit-load-images',
                palletId: palletId,
                subCategory: subCategory,
                isLoading: false,
                hasError: true,
                errorMessage: errorMessage,
                processingCompress: false
            };

            // Update local state with error
            setSlotMedia(prev => ({ ...prev, [subCategory]: errorItem }));

            // Show error toast
            showErrorToast(errorMessage);

            return null;
        } finally {
            // Reset uploading state and compression state
            setUploading(prev => ({ ...prev, [subCategory]: false }));
            setSlotMedia(prev => {
                const item = prev[subCategory];
                if (!item) return prev;

                // If item has processingCompress flag, clear it
                if (item.processingCompress) {
                    return {
                        ...prev,
                        [subCategory]: {
                            ...item,
                            processingCompress: false
                        }
                    };
                }

                return prev;
            });
        }
    };

    // Helper to get content type from file
    const getContentTypeFromFile = (file: File): MediaItem['contentType'] => {
        if (file.type.startsWith('image/')) return 'image';
        if (file.type.startsWith('video/')) return 'video';
        if (file.type === 'application/pdf') return 'pdf';
        return 'image'; // Default fallback
    };

    // Helper to get the title for a slot
    const getSlotTitle = (subCategory: NonNullable<MediaItem['subCategory']>): string => {
        const slot = uploadSlots.find(s => s.key === subCategory);
        return slot ? slot.title : subCategory;
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, subCategory: NonNullable<MediaItem['subCategory']>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file
        const validation = validateFile(file, subCategory);
        if (!validation.valid) {
            showErrorToast(validation.error || 'Invalid file');
            // Reset the input value
            event.target.value = '';
            return;
        }

        // Handle existing item replacement
        const existingItem = slotMedia[subCategory];
        if (existingItem && !existingItem.file && !existingItem.hasError) {
            // For existing DB items, we'll let the server handle the replacement
            // The API will replace based on subCategory and palletId
        }

        // Show temporary preview while uploading
        const previewUrl = getPreviewUrl(file);
        const tempItem: MediaItem = {
            id: Date.now(), // Temporary ID
            url: previewUrl,
            localUrl: previewUrl,
            contentType: getContentTypeFromFile(file),
            type: 'unit-load-images',
            palletId: palletId,
            file: file,
            subCategory: subCategory,
            isLoading: true,
            hasError: false,
            processingCompress: false
        };

        // Update UI immediately with loading state
        setSlotMedia(prev => ({ ...prev, [subCategory]: tempItem }));

        // Start upload process
        await uploadFile(file, subCategory);

        // Reset input to allow selecting the same file again
        event.target.value = '';
    };

    const handleDelete = async (subCategory: NonNullable<MediaItem['subCategory']>) => {
        const itemToDelete = slotMedia[subCategory];
        if (!itemToDelete) return;

        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete the ${getSlotTitle(subCategory)}?`)) {
            return;
        }

        try {
            // Set uploading state to prevent interactions during deletion
            setUploading(prev => ({ ...prev, [subCategory]: true }));

            // Revoke blob URL if it exists
            if (itemToDelete.localUrl && itemToDelete.localUrl.startsWith('blob:')) {
                URL.revokeObjectURL(itemToDelete.localUrl);
            }

            // Only call API for items with IDs from the server
            if (itemToDelete.id && !itemToDelete.file) {
                const oktaUser = await oktaService.getUser();
                if (!oktaUser || !oktaUser.access_token) {
                    throw new Error('Not authenticated. Please log in again.');
                }

                // Call the delete API
                const response = await fetch(`${backendApiUrl}/media/${itemToDelete.id}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${oktaUser.access_token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error(`Failed to delete: ${response.status} ${response.statusText}`);
                }

                // Notify parent component about deletion
                onDeleteMedia(itemToDelete.id);
            }

            // Clear from local state
            setSlotMedia(prev => ({ ...prev, [subCategory]: null }));
            refetch()
            // Show success message
            showSuccessToast(`${getSlotTitle(subCategory)} deleted successfully`);
        } catch (error) {
            console.error('Delete error:', error);
            showErrorToast(error instanceof Error ? error.message : 'Failed to delete');
        } finally {
            // Reset uploading state
            setUploading(prev => ({ ...prev, [subCategory]: false }));
        }
    };

    // Helper to render file type icon based on content type
    const renderFileIcon = (contentType: MediaItem['contentType']) => {
        switch (contentType) {
            case 'pdf':
                return <FileText size={24} className="text-red-500" />;
            case 'image':
                return <ImageIcon size={24} className="text-blue-500" />;
            case 'video':
                return <FileText size={24} className="text-green-500" />;
            default:
                return <FileText size={24} />;
        }
    };

    // Render preview content based on file type
    const renderPreview = (item: MediaItem) => {
        if (item.isLoading || item.processingCompress) {
            return (
                <div className="flex flex-col items-center justify-center h-full w-full">
                    <Loader2 size={48} className="animate-spin text-blue-500 mb-2" />
                    <p className="text-sm text-blue-500">{item.processingCompress ? 'Compressing...' : 'Loading...'}</p>
                    {item.processingCompress && (
                        <p className="text-xs text-gray-500 mt-2 max-w-[80%] text-center">
                            Large images are being compressed for optimal performance
                        </p>
                    )}
                </div>
            );
        }

        if (item.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-full w-full">
                    <AlertCircle size={48} className="text-red-500 mb-2" />
                    <p className="text-sm text-red-500 text-center">
                        {item.errorMessage || 'Failed to load'}
                    </p>
                </div>
            );
        }

        if (item.contentType === 'pdf') {
            return (
                <div className="flex flex-col items-center justify-center h-full w-full bg-gray-100 p-4 rounded-md">
                    <FileText size={64} className="text-red-500 mb-2" />
                    <p className="text-sm font-semibold text-center truncate max-w-full">
                        {item.originalFilename || 'PDF Document'}
                    </p>
                    {item.fileSize && (
                        <p className="text-xs text-gray-500">
                            {(item.fileSize / (1024 * 1024)).toFixed(2)} MB
                        </p>
                    )}
                </div>
            );
        }

        // For images, show the actual image
        return (
            <img
                src={item.localUrl || getFullMediaUrl(item.url)}
                alt={getSlotTitle(item.subCategory!)}
                className="max-w-full max-h-full object-contain rounded-md"
                onError={(e) => {
                    console.error(`Failed to load image: ${item.url}`);
                    // Find the subCategory for this item
                    const subCategory = item.subCategory;
                    if (subCategory) {
                        updateSlotMediaLoadingState(subCategory, false, true, 'Failed to load image');
                    }
                }}
            />
        );
    };

    // Add handleDownload function
    const handleDownload = async (url: string, contentType: MediaItem['contentType'], fileName?: string) => {
        try {

            const fullUrl = getFullMediaUrl(url);
            console.log('handle downnlload cllicked', fullUrl)
            if (!fullUrl.startsWith('blob:')) {
                console.log('inside if  block')
                // Use the same URL handling as in upload-handler.tsx

                console.log('fullUrl', fullUrl)
                console.log(`Downloading from URL: ${fullUrl}`);

                // For proxy URLs, we need to add a download parameter
                const downloadUrl = fullUrl.includes('/api/media/image/')
                    ? `${fullUrl}?download=true`
                    : fullUrl;

                // Get Okta token for authorization
                const headers: Record<string, string> = {
                    'Accept': 'application/json, image/*, video/*, application/pdf, application/octet-stream'
                };

                try {
                    const oktaUser = await oktaService.getUser();
                    if (oktaUser.access_token) {
                        headers['Authorization'] = `Bearer ${oktaUser.access_token}`;
                        console.log('Added authorization header for download');
                    }
                } catch (tokenError) {
                    console.warn('Could not get Okta token for download:', tokenError);
                    // Continue without auth header - might still work for public resources
                }

                const response = await fetch(downloadUrl, {
                    headers,
                    credentials: 'include' // Include cookies if needed
                });

                if (!response.ok) {
                    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
                }

                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;

                // Determine extension based on content type
                const extension = contentType === 'image' ? '.jpg' : contentType === 'video' ? '.mp4' : contentType === 'pdf' ? '.pdf' : '.bin';

                // Use provided file name or generate one
                link.download = fileName || `${contentType}-${Date.now()}${extension}`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);

                showSuccessToast('File downloaded successfully');
            } else {
                console.log('inside else block')
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            showErrorToast('Failed to download file');
        }
    };

    // Add a getFullMediaUrl function that matches the one in upload-handler.tsx
    const getFullMediaUrl = (url: string): string => {
        // If it's a blob URL or already absolute URL (starts with http/https), use as is
        if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
            return url;
        }

        // For API media image endpoints, we need special handling
        if (url.startsWith('/api/media/image/')) {
            // This is already a properly formatted proxy URL, we just need to make sure it points to the backend
            // backendApiUrl already includes /api, so we need to remove that from our url
            const blobName = url.split('/api/media/image/')[1];
            const fullUrl = `${backendApiUrl}/media/image/${blobName}`;
            console.log(`🖼️ Image proxy URL: ${url} -> ${fullUrl}`);
            return fullUrl;
        }

        // If it's a relative URL (like /api/something), prepend the API URL properly
        if (url.startsWith('/api/')) {
            const fullUrl = `${backendApiUrl}${url.substring(4)}`;
            console.log(`🖼️ API URL: ${url} -> ${fullUrl}`);
            return fullUrl;
        }

        // For other relative URLs
        if (url.startsWith('/')) {
            const fullUrl = `${backendApiUrl}${url}`;
            console.log(`🖼️ Relative URL: ${url} -> ${fullUrl}`);
            return fullUrl;
        }

        // Default case, just return the URL
        console.log(`🖼️ Using original URL: ${url}`);
        return url;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5">
            {uploadSlots.map(slot => {
                const currentMedia = slotMedia[slot.key];
                const isUploading = uploading[slot.key];

                return (
                    <div
                        key={slot.key}
                        className={cn(
                            "border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center aspect-square relative group",
                            currentMedia
                                ? currentMedia.hasError
                                    ? "border-red-300"
                                    : "border-gray-300"
                                : isUploading
                                    ? "border-blue-300"
                                    : "border-gray-400 hover:border-blue-500 cursor-pointer"
                        )}
                        onClick={() => !currentMedia && !isUploading && handleCardClick(slot.key)}
                    >
                        {currentMedia ? (
                            // Display file preview or error state
                            <>
                                {renderPreview(currentMedia)}

                                {/* File type indicator for non-image files */}
                                {currentMedia.contentType !== 'image' && !currentMedia.isLoading && !currentMedia.hasError && (
                                    <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                        {renderFileIcon(currentMedia.contentType)}
                                    </div>
                                )}

                                {/* Dropdown Menu for Actions */}
                                {!currentMedia.isLoading && !currentMedia.hasError && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger
                                            className={cn(
                                                "rounded-full text-white p-1 font-semibold text-sm flex capitalize items-center gap-2 outline-none absolute right-4 top-4 bg-gray-600"
                                            )}
                                        >
                                            <span>
                                                <Ellipsis size={20} />
                                            </span>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className="rounded-lg p-0">
                                            {/* Download option */}
                                            <DropdownMenuItem
                                                className="p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownload(
                                                        currentMedia.url, currentMedia.contentType
                                                    );
                                                }}
                                            >
                                                <span>
                                                    <Download size={20} />
                                                </span>
                                                &nbsp; Download
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator className="bg-border-style-1" />

                                            {/* Replace option */}
                                            <DropdownMenuItem
                                                className="p-1"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCardClick(slot.key);
                                                }}
                                            >
                                                <span>
                                                    <RefreshCcw size={20} />
                                                </span>
                                                &nbsp; Replace
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator className="bg-border-style-1" />

                                            {/* View option */}
                                            <FullscreenViewDialog
                                                mediaUrl={currentMedia.localUrl || getFullMediaUrl(currentMedia.url)}
                                                isVideo={currentMedia.contentType === 'video'}
                                                isPdf={currentMedia.contentType === 'pdf'}
                                            />
                                            <DropdownMenuSeparator className="bg-border-style-1" />

                                            {/* Delete option */}
                                            <DropdownMenuItem
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(slot.key);
                                                }}
                                                className="text-states-error p-1"
                                            >
                                                <span>
                                                    <Trash size={20} />
                                                </span>{" "}
                                                &nbsp; Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                )}

                                {/* Retry Button for Error States */}
                                {currentMedia.hasError && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCardClick(slot.key);
                                        }}
                                        className="mt-4 bg-blue-500 text-white rounded-md px-3 py-1.5 text-sm hover:bg-blue-600"
                                    >
                                        Try Again
                                    </button>
                                )}
                            </>
                        ) : (
                            // Display Upload Prompt
                            <div className="text-center text-gray-500">
                                {isUploading ? (
                                    <>
                                        <Loader2 size={48} className="animate-spin mx-auto mb-2 text-blue-500" />
                                        <p className="font-semibold text-md mb-1 text-blue-500">Uploading...</p>
                                    </>
                                ) : (
                                    <>
                                        {slot.icon || <UploadCloud size={48} className="mx-auto mb-2" />}
                                        <p className="font-semibold text-md mb-1">{slot.title}</p>
                                        <p className="text-sm text-gray-400">{slot.description}</p>
                                        <p className="text-sm mt-2">Click to upload</p>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Hidden File Input with proper accept types */}
                        <input
                            type="file"
                            ref={fileInputRefs[slot.key]}
                            style={{ display: 'none' }}
                            accept={slot.acceptTypes}
                            onChange={(e) => handleFileChange(e, slot.key)}
                            aria-label={`Upload ${slot.title}`}
                            disabled={isUploading}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default UnitLoadImageUploader; 